import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
  Tag,
} from 'antd';
import { Device, DeviceTag, RemoveTagPayload } from 'types/devices';
import { AddLabel } from './AddTag';
import { useState } from 'react';
import {
  removeDeviceTag,
  changeDeviceStatus,
  updateDevicePushVersion,
} from 'api/devices.api';
import openNotification from 'hooks/notificationHook';
import { useAppDispatch } from 'hooks/reduxHooks';
import { deviceStatusChanged, doList } from 'store/slices/devicesSlice';
import * as Sentry from '@sentry/react';
import notificationHook from 'hooks/notificationHook';
import statusMessages from 'statusMessages';
import { ApiError } from 'types';
import { TableEditButton } from 'components/TableEditButton/TableEditButton';
import styles from './EditDeviceModal.module.scss';

export const EditDeviceModal = ({ device }: { device: Device }) => {
  const [showDeleteTagModal, setShowDeleteTagModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<DeviceTag>();
  const [removingTag, setRemovingTag] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [updatingPushVersion, setUpdatingPushVersion] = useState(false);
  const [form] = Form.useForm<{ pushVer: string }>();
  const dispatch = useAppDispatch();
  const pushVersionOptions = [
    {
      label: 'v2',
      value: device.pushVer.startsWith('2.') ? device.pushVer : '2.0.33S',
    },
    {
      label: 'v3',
      value: device.pushVer.startsWith('3.') ? device.pushVer : '3.1.2',
    },
  ];

  const updatePushVersion = async ({ pushVer }: { pushVer: string }) => {
    setUpdatingPushVersion(true);
    try {
      await updateDevicePushVersion(device._id, { pushVer: pushVer.trim() });
      await dispatch(doList());
      notificationHook({
        type: 'success',
        message: 'Versión PUSH actualizada con éxito.',
      });
      setIsEditing(false);
    } catch (error) {
      notificationHook({
        type: 'error',
        message: 'No fue posible actualizar la versión PUSH.',
      });
      Sentry.captureException(error);
    } finally {
      setUpdatingPushVersion(false);
    }
  };

  const handleChangeDeviceStatus = async () => {
    openNotification({
      type: 'warning',
      placement: 'top',
      message: 'Cambiar estatus',
      description: `¿Confirmas que deseas ${device.status ? 'Desactivar' : 'Activar'} el dispositivo?`,
      duration: null,
      showConfirmBtn: true,
      onConfirm: async () => {
        try {
          await changeDeviceStatus(device._id);
          dispatch(deviceStatusChanged());
          await dispatch(doList());
        } catch (error) {
          notificationHook({
            type: 'error',
            message: 'No fue posible cambiar el estatus del dispositivo.',
          });
          Sentry.captureException(error);
        }
      },
      confirmBtnLabel: `${device?.status ? 'Desactivar' : 'Activar'}`,
    });
  };

  const handleSwitchChange = () => {
    handleChangeDeviceStatus();
  };

  const deleteTag = async (tagId: string) => {
    setRemovingTag(true);
    const payload: RemoveTagPayload = {
      deviceId: device._id,
      tagId,
    };
    await removeDeviceTag(payload)
      .then(() => {
        setRemovingTag(false);
        dispatch(doList());
        notificationHook({
          type: 'success',
          message: 'Etiqueta eliminada con éxito.',
        });
      })
      .catch((error) => {
        setRemovingTag(false);
        const currentStatus = (error as ApiError).statusCode;
        notificationHook({
          type: 'error',
          message: statusMessages.devices[
            currentStatus ? currentStatus : 500
          ] as string,
        });
        Sentry.captureException(error);
      });
  };

  return (
    <>
      <TableEditButton
        dataCy="edit-device-button"
        onClick={() => {
          form.setFieldsValue({ pushVer: device.pushVer || '' });
          setIsEditing(true);
        }}
      />
      <Modal
        title="Editar dispositivo"
        open={isEditing}
        destroyOnHidden
        data-cy="edit-device-modal"
        onCancel={() => {
          setIsEditing(false);
        }}
        width="50vw"
        footer={null}
      >
        <>
          <Form form={form} labelCol={{ span: 5 }} onFinish={updatePushVersion}>
            <Form.Item label="Número de serie">
              <Input disabled value={device.serialNumber} />
            </Form.Item>
            <Form.Item label="Nombre del dispositivo">
              <Input disabled value={device.configs.DeviceName} />
            </Form.Item>
            <Form.Item label="IP del dispositivo">
              <Input disabled value={device.properties.IPAddress} />
            </Form.Item>
            <Form.Item
              label="Versión PUSH"
              name="pushVer"
              extra={`Origen actual: ${
                device.pushVerSource === 'manual'
                  ? 'configuración manual'
                  : 'handshake del dispositivo'
              }. Al guardar, el valor se conservará como una configuración manual.`}
              rules={[
                {
                  required: true,
                  message: 'Selecciona la versión PUSH.',
                },
              ]}
            >
              <Select
                options={pushVersionOptions}
                placeholder="Selecciona v2 o v3"
                data-cy="device-push-version-select"
              />
            </Form.Item>
            <Form.Item label="Etiquetas" data-cy="device-tags-edit-form">
              {device.tags && device.tags.length > 0 ? (
                <>
                  {device.tags.map((tag: DeviceTag) => (
                    <Tag
                      key={tag._id}
                      color={tag.tagColor}
                      variant="solid"
                      closable
                      data-cy="device-tag"
                      onClose={(e) => {
                        e.preventDefault();
                        setTagToDelete(tag);
                        setShowDeleteTagModal(true);
                      }}
                    >
                      {tag.tagName}
                    </Tag>
                  ))}
                </>
              ) : (
                <p className={styles.emptyText}>Sin etiquetas</p>
              )}
            </Form.Item>
            <Form.Item label="Activar dispositivo">
              <Switch
                onChange={handleSwitchChange}
                checked={device.status}
                id="status"
                data-cy="status-switch"
                disabled={!device}
              />
            </Form.Item>
            <Form.Item wrapperCol={{ offset: 5 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={updatingPushVersion}
                data-cy="save-device-push-version"
              >
                Guardar versión PUSH
              </Button>
            </Form.Item>
          </Form>
          <AddLabel deviceId={device._id} />
        </>
      </Modal>
      <Modal
        title="¿Confirmas que deseas remover la etiqueta?"
        open={showDeleteTagModal}
        okText="Confirmar"
        cancelText="Cancelar"
        confirmLoading={removingTag}
        onOk={() => {
          if (tagToDelete && tagToDelete._id) {
            deleteTag(tagToDelete._id);
            setShowDeleteTagModal(false);
          }
        }}
        onCancel={() => {
          setShowDeleteTagModal(false);
        }}
      >
        <Row>
          <Col xs={24}>
            {tagToDelete && device && (
              <p>
                Al confirmar, se eliminará la etiqueta{' '}
                <Tag color={tagToDelete.tagColor} variant="solid">
                  {tagToDelete.tagName}
                </Tag>
                del dispositivo <b>{device.configs.SerialNumber}</b>
              </p>
            )}
          </Col>
        </Row>
      </Modal>
    </>
  );
};
